import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Database, Cloud, Globe, Mail, MessageSquare, Apple, Play, Radio, Plus, Trash2, CalendarClock, DollarSign, Loader2, AlertTriangle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api from "@/lib/api";
import { fmtMoney, fmtNum, fmtFechaCorta, diasHasta } from "@/lib/format";
import { toast } from "sonner";

const Estado = ({ estado }) => {
  const map = {
    ok: { c: "#4ECDC4", t: "Operativo" },
    pendiente_publicacion: { c: "#F39C12", t: "Pendiente de publicación en tiendas" },
  };
  const s = map[estado] || { c: "#71717A", t: estado || "—" };
  return <span className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider" style={{ color: s.c }}>
    <span className="w-2 h-2 rounded-full" style={{ backgroundColor: s.c }} />{s.t}
  </span>;
};

const ServiceCard = ({ icon: Icon, title, estado, children, testid }) => (
  <div data-testid={testid} className="rounded-xl bg-[#0A0A0A] border border-[#1F1F24] p-5 hover:border-[#D4AF37]/25 transition-colors">
    <div className="flex items-center justify-between mb-3">
      <div className="flex items-center gap-2">
        <Icon className="w-4 h-4 text-[#D4AF37]" />
        <h3 className="text-sm text-[#F5F5F7] font-medium">{title}</h3>
      </div>
    </div>
    <Estado estado={estado} />
    <div className="mt-3 space-y-1.5 text-xs">{children}</div>
  </div>
);

const Line2 = ({ label, value, color }) => (
  <div className="flex justify-between">
    <span className="text-[#71717A]">{label}</span>
    <span className="font-mono-ax" style={{ color: color || "#F5F5F7" }}>{value}</span>
  </div>
);

const SalaMaquinas = () => {
  const [infra, setInfra] = useState(null);
  const [finanzas, setFinanzas] = useState(null);
  const [form, setForm] = useState({ nombre: "", plataforma: "", costo: "", ciclo: "mensual", proximo_cobro: "", pago_automatico: false, notas: "" });
  const [saving, setSaving] = useState(false);

  const cargar = useCallback(async () => {
    try {
      const [i, f] = await Promise.all([api.get("/comando/infraestructura"), api.get("/comando/finanzas")]);
      setInfra(i.data);
      setFinanzas(f.data);
    } catch (e) { /* noop */ }
  }, []);

  useEffect(() => {
    document.title = "Sala de Máquinas · AXIORA";
    cargar();
    const t = setInterval(cargar, 30000);
    return () => clearInterval(t);
  }, [cargar]);

  const pendienteI = infra?.puente_pendiente;
  const mongo = infra?.mongodb || {};
  const scrapfly = infra?.scrapfly || {};
  const zenrows = infra?.zenrows || {};
  const sendgrid = infra?.sendgrid || {};
  const twilio = infra?.twilio || {};

  const items = finanzas?.items || [];
  const totalAnual = useMemo(() => items.reduce((acc, it) => acc + (it.ciclo === "anual" ? Number(it.costo || 0) : Number(it.costo || 0) * 12), 0), [items]);
  const vencimientos = useMemo(() => [...items].filter(i => i.proximo_cobro).sort((a, b) => new Date(a.proximo_cobro) - new Date(b.proximo_cobro)), [items]);
  const alertas = vencimientos.filter((i) => { const d = diasHasta(i.proximo_cobro); return d !== null && d <= 5; });

  const guardar = async () => {
    if (!form.nombre.trim() || !form.costo) { toast.error("Nombre y costo son obligatorios"); return; }
    setSaving(true);
    try {
      const res = await api.post("/comando/finanzas", { ...form, costo: Number(form.costo) });
      if (res.data?.puente_pendiente) { toast.error("PUENTE PENDIENTE — no se pudo guardar"); }
      else { toast.success("Servicio guardado"); setForm({ nombre: "", plataforma: "", costo: "", ciclo: "mensual", proximo_cobro: "", pago_automatico: false, notas: "" }); cargar(); }
    } catch (e) { toast.error("No se pudo guardar el servicio"); }
    finally { setSaving(false); }
  };

  const eliminar = async (it) => {
    try {
      const res = await api.post("/comando/finanzas", { ...it, _eliminar: true });
      if (res.data?.puente_pendiente) toast.error("PUENTE PENDIENTE");
      else { toast.success("Servicio eliminado"); cargar(); }
    } catch (e) { toast.error("No se pudo eliminar"); }
  };

  return (
    <div className="p-6">
      <header className="mb-5">
        <h2 className="font-serif-ax text-4xl text-[#F5F5F7] tracking-wide">Sala de Máquinas</h2>
        <p className="text-xs text-[#71717A] mt-0.5">Salud de servicios · finanzas · en vivo</p>
      </header>

      {pendienteI && (
        <div data-testid="sala-pendiente" className="mb-4 flex items-center gap-2 px-4 py-3 rounded-lg bg-[#F39C12]/10 border border-[#F39C12]/30 text-[#F39C12] text-sm">
          <Radio className="w-4 h-4" /> PUENTE PENDIENTE — datos de infraestructura no disponibles.
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mb-8">
        <ServiceCard icon={Database} title="MongoDB" estado={mongo.estado} testid="svc-mongodb">
          <Line2 label="Tamaño de datos" value={mongo.tamano_datos_mb != null ? `${mongo.tamano_datos_mb} MB` : "—"} />
          <Line2 label="Colecciones" value={fmtNum(mongo.total_colecciones)} />
          <Line2 label="Documentos" value={fmtNum(mongo.total_documentos)} color="#D4AF37" />
        </ServiceCard>
        <ServiceCard icon={Cloud} title="Scrapfly" estado={scrapfly.estado} testid="svc-scrapfly">
          <Line2 label="Plan" value={scrapfly.plan || "—"} />
          <Line2 label="Créditos usados" value={fmtNum(scrapfly.creditos_usados)} />
          <Line2 label="Créditos restantes" value={fmtNum(scrapfly.creditos_restantes)} color="#4ECDC4" />
        </ServiceCard>
        <ServiceCard icon={Globe} title="ZenRows" estado={zenrows.estado} testid="svc-zenrows">
          <p className="text-[#71717A] leading-relaxed">{zenrows.nota || "—"}</p>
        </ServiceCard>
        <ServiceCard icon={Mail} title="SendGrid" estado={sendgrid.estado} testid="svc-sendgrid">
          <Line2 label="Mes" value={sendgrid.mes || "—"} />
          <Line2 label="Enviados" value={fmtNum(sendgrid.enviados)} />
          <Line2 label="Entregados" value={fmtNum(sendgrid.entregados)} color="#4ECDC4" />
          <Line2 label="Rebotes" value={fmtNum(sendgrid.rebotes)} color="#FF6B5B" />
        </ServiceCard>
        <ServiceCard icon={MessageSquare} title="Twilio" estado={twilio.estado} testid="svc-twilio">
          <Line2 label="Saldo" value={twilio.saldo != null ? `${twilio.saldo} ${twilio.moneda || ""}` : "—"} color="#D4AF37" />
        </ServiceCard>
        <ServiceCard icon={Apple} title="Apple IAP" estado={infra?.apple_iap?.estado || "pendiente_publicacion"} testid="svc-apple" />
        <ServiceCard icon={Play} title="Google Play Billing" estado={infra?.google_billing?.estado || "pendiente_publicacion"} testid="svc-google" />
      </div>

      {/* FINANZAS */}
      <div className="mb-4 flex items-center gap-2">
        <DollarSign className="w-5 h-5 text-[#D4AF37]" />
        <h3 className="font-serif-ax text-2xl text-[#F5F5F7]">Finanzas</h3>
      </div>

      {finanzas?.puente_pendiente && (
        <div className="mb-4 flex items-center gap-2 px-4 py-3 rounded-lg bg-[#F39C12]/10 border border-[#F39C12]/30 text-[#F39C12] text-sm">
          <Radio className="w-4 h-4" /> PUENTE PENDIENTE — no se pueden cargar/guardar servicios.
        </div>
      )}

      {alertas.length > 0 && (
        <div data-testid="alerta-vencimientos" className="mb-4 flex items-start gap-2 px-4 py-3 rounded-lg bg-[#F39C12]/12 border border-[#F39C12]/40 text-[#F39C12] text-sm">
          <AlertTriangle className="w-4 h-4 mt-0.5" />
          <div>
            <p className="font-medium">Pagos que vencen en 5 días o menos:</p>
            <ul className="mt-1 space-y-0.5">
              {alertas.map((a, i) => (
                <li key={i} className="text-xs">• {a.nombre} — {fmtFechaCorta(a.proximo_cobro)} ({diasHasta(a.proximo_cobro)}d) · {fmtMoney(a.costo)}</li>
              ))}
            </ul>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Formulario */}
        <div className="rounded-xl bg-[#0A0A0A] border border-[#1F1F24] p-5">
          <h4 className="text-sm text-[#F5F5F7] uppercase tracking-wider mb-4 flex items-center gap-2"><Plus className="w-4 h-4" /> Registrar servicio</h4>
          <div className="space-y-3">
            <Field label="Nombre">
              <Input data-testid="fin-nombre" value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} className="bg-[#050505] border-[#1F1F24] text-[#F5F5F7] focus-visible:ring-[#D4AF37]" />
            </Field>
            <Field label="Plataforma / URL de acceso">
              <Input data-testid="fin-plataforma" value={form.plataforma} onChange={(e) => setForm({ ...form, plataforma: e.target.value })} className="bg-[#050505] border-[#1F1F24] text-[#F5F5F7] focus-visible:ring-[#D4AF37]" />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Costo (USD)">
                <Input data-testid="fin-costo" type="number" value={form.costo} onChange={(e) => setForm({ ...form, costo: e.target.value })} className="bg-[#050505] border-[#1F1F24] text-[#F5F5F7] font-mono-ax focus-visible:ring-[#D4AF37]" />
              </Field>
              <Field label="Ciclo">
                <Select value={form.ciclo} onValueChange={(v) => setForm({ ...form, ciclo: v })}>
                  <SelectTrigger data-testid="fin-ciclo" className="bg-[#050505] border-[#1F1F24] text-[#F5F5F7]"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#0A0A0A] border-[#1F1F24] text-[#F5F5F7]">
                    <SelectItem value="mensual">Mensual</SelectItem>
                    <SelectItem value="anual">Anual</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
            </div>
            <Field label="Próximo cobro">
              <Input data-testid="fin-fecha" type="date" value={form.proximo_cobro} onChange={(e) => setForm({ ...form, proximo_cobro: e.target.value })} className="bg-[#050505] border-[#1F1F24] text-[#F5F5F7] font-mono-ax focus-visible:ring-[#D4AF37]" />
            </Field>
            <div className="flex items-center justify-between py-1">
              <span className="text-xs text-[#A1A1AA]">¿Pago automático?</span>
              <Switch data-testid="fin-auto" checked={form.pago_automatico} onCheckedChange={(v) => setForm({ ...form, pago_automatico: v })} />
            </div>
            <Field label="Notas">
              <Input data-testid="fin-notas" value={form.notas} onChange={(e) => setForm({ ...form, notas: e.target.value })} className="bg-[#050505] border-[#1F1F24] text-[#F5F5F7] focus-visible:ring-[#D4AF37]" />
            </Field>
            <button data-testid="fin-guardar" onClick={guardar} disabled={saving}
              className="w-full h-11 rounded-lg bg-gradient-to-r from-[#D4AF37] to-[#FFD700] text-[#0A0A0A] font-semibold flex items-center justify-center gap-2 hover:opacity-90 disabled:opacity-50">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Guardar servicio
            </button>
          </div>
        </div>

        {/* Resumen + lista */}
        <div className="lg:col-span-2 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="rounded-xl bg-[#0A0A0A] border border-[#1F1F24] p-5">
              <p className="text-[10px] text-[#71717A] uppercase tracking-wider mb-1">Total anual estimado</p>
              <p className="font-mono-ax text-3xl text-[#FFD700]">{fmtMoney(totalAnual)}</p>
            </div>
            <div className="rounded-xl bg-[#0A0A0A] border border-[#1F1F24] p-5">
              <p className="text-[10px] text-[#71717A] uppercase tracking-wider mb-1">Servicios contratados</p>
              <p className="font-mono-ax text-3xl text-[#F5F5F7]">{fmtNum(items.length)}</p>
            </div>
          </div>

          <div className="rounded-xl bg-[#0A0A0A] border border-[#1F1F24] overflow-hidden">
            <div className="px-4 py-3 border-b border-[#1F1F24] flex items-center gap-2">
              <CalendarClock className="w-4 h-4 text-[#D4AF37]" />
              <h4 className="text-sm text-[#F5F5F7] uppercase tracking-wider">Próximos vencimientos</h4>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#050505]">
                  <tr className="text-[10px] uppercase tracking-wider text-[#71717A]">
                    <th className="text-left px-3 py-2">Servicio</th>
                    <th className="text-left px-3 py-2">Próximo cobro</th>
                    <th className="text-left px-3 py-2">Costo</th>
                    <th className="text-left px-3 py-2">Ciclo</th>
                    <th className="text-left px-3 py-2">Auto</th>
                    <th className="px-3 py-2" />
                  </tr>
                </thead>
                <tbody>
                  {vencimientos.length ? vencimientos.map((it, i) => {
                    const d = diasHasta(it.proximo_cobro);
                    const alerta = d !== null && d <= 5;
                    return (
                      <tr key={it.id || i} data-testid={`fin-row-${i}`} className="border-t border-[#1F1F24]">
                        <td className="px-3 py-2.5 text-[#F5F5F7]">{it.nombre}</td>
                        <td className={`px-3 py-2.5 font-mono-ax text-xs ${alerta ? "text-[#F39C12]" : "text-[#A1A1AA]"}`}>{fmtFechaCorta(it.proximo_cobro)}{alerta ? ` · ${d}d` : ""}</td>
                        <td className="px-3 py-2.5 font-mono-ax text-xs text-[#FFD700]">{fmtMoney(it.costo)}</td>
                        <td className="px-3 py-2.5 text-[#A1A1AA] text-xs capitalize">{it.ciclo}</td>
                        <td className="px-3 py-2.5 text-xs">{it.pago_automatico ? <span className="text-[#4ECDC4]">Sí</span> : <span className="text-[#71717A]">No</span>}</td>
                        <td className="px-3 py-2.5 text-right">
                          <button data-testid={`fin-eliminar-${i}`} onClick={() => eliminar(it)} className="text-[#71717A] hover:text-[#FF6B5B]"><Trash2 className="w-4 h-4" /></button>
                        </td>
                      </tr>
                    );
                  }) : (
                    <tr><td colSpan={6} className="px-3 py-8 text-center text-[#52525b] text-sm">Aún no hay servicios registrados.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const Field = ({ label, children }) => (
  <div>
    <label className="text-[10px] text-[#71717A] uppercase tracking-wider mb-1.5 block">{label}</label>
    {children}
  </div>
);

export default SalaMaquinas;
